/**
 * 조직별 브랜드 목록 페이지
 * HomeContent와 완전히 동일한 디자인
 */
import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tag } from 'lucide-react';
import { SearchBar } from '@/components/SearchBar';
import { BrandCard } from '@/components/cards/BrandCard';
import { useOrganizationBrands } from '@/hooks/useOrganizationBrands';

export function BrandsPage() {
  const navigate = useNavigate();
  // useParams로 URL 파라미터 추출 - Routes/Route 구조에서 정상 작동
  const { orgParam } = useParams<{ orgParam: string }>();
  const { brands, organization, loading, error } = useOrganizationBrands(orgParam);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredBrands = useMemo(() => {
    if (!searchQuery.trim()) return brands;
    const query = searchQuery.toLowerCase();
    return brands.filter(
      (brand) =>
        brand.name?.toLowerCase().includes(query) ||
        brand.description?.toLowerCase().includes(query)
    );
  }, [brands, searchQuery]);

  const handleBrandClick = (brandSlug: string) => {
    navigate(`/org/${orgParam}/brand/${brandSlug}`);
  };

  return (
    <div className="flex-1 pb-[calc(5rem+2vw)] p-[0px] m-[0px]">
      <SearchBar
        placeholder="Buscar marcas..."
        value={searchQuery}
        onChange={setSearchQuery}
      />

      <div className="p-[min(5vw,1.25rem)]">
        <div className="flex items-center justify-between m-[0px]">
          <h2 className="text-[16px] font-medium text-foreground">
            {organization?.name || 'Cargando...'}
          </h2>
        </div>
        <p className="text-[14px] text-muted-foreground mb-[min(3vw,1rem)]">
          {filteredBrands.length} marcas
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">Cargando...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">{error}</p>
          </div>
        ) : filteredBrands.length === 0 ? (
          <div className="text-center py-8">
            <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery
                ? 'No se encontraron marcas'
                : 'No hay marcas disponibles'}
            </p>
          </div>
        ) : (
          <div className="space-y-[min(2vw,0.5rem)]">
            {filteredBrands.map((brand, index) => (
              <div
                key={brand.id}
                className="transform transition-all duration-500 ease-out opacity-0 translate-y-4 animate-fadeInUp"
                style={{
                  animationDelay: `${index * 100}ms`,
                  animationFillMode: 'forwards',
                }}
              >
                <div className="hover:scale-[1.02] transition-transform duration-200 hover:shadow-lg rounded-2xl">
                  <BrandCard
                    id={brand.id}
                    slug={brand.slug}
                    name={brand.name}
                    description={brand.description}
                    logoUrl={brand.logo_url}
                    storesCount={brand.stores_count}
                    isActive={brand.is_active}
                    onClick={handleBrandClick}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
